/**
 * FASE G — Kanban Board per /admin/richieste.
 *
 * 5 colonne fisse mappate agli status unificati:
 *   nuovo · in_lavorazione · preventivato (label "In attesa cliente") ·
 *   chiuso_positivo · chiuso_negativo
 *
 * `archiviato` NON è mostrato (toggle "Mostra archiviate" nella tabella).
 *
 * Interazione: drag-and-drop con `@dnd-kit/core` + optimistic UI + undo toast.
 * Ogni drop chiama `POST /admin/richieste/bulk-action`.
 */
import React, { useCallback, useMemo, useState } from "react";
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
  useDroppable, useDraggable, closestCenter,
} from "@dnd-kit/core";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText, Eye, Undo2, Flame, GripVertical,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Colonne Kanban (order matters)
export const KANBAN_COLUMNS = [
  { key: "nuovo",           label: "Nuovo",             color: "#3B82F6" },
  { key: "in_lavorazione",  label: "In lavorazione",    color: "#F59E0B" },
  { key: "preventivato",    label: "In attesa cliente", color: "#F97316" },
  { key: "chiuso_positivo", label: "Chiuso positivo",   color: "#10B981" },
  { key: "chiuso_negativo", label: "Chiuso negativo",   color: "#6B7280" },
];

const RAMO_BADGE = {
  d_o:              { l: "D&O",       c: "bg-indigo-100 text-indigo-700" },
  cyber:            { l: "Cyber",     c: "bg-purple-100 text-purple-700" },
  fine_arts:        { l: "Fine Arts", c: "bg-pink-100 text-pink-700" },
  convenzioni_auto: { l: "RCA",       c: "bg-emerald-100 text-emerald-700" },
  casa:             { l: "Casa",      c: "bg-teal-100 text-teal-700" },
  infortuni:        { l: "Infortuni", c: "bg-orange-100 text-orange-700" },
  vita:             { l: "Vita",      c: "bg-yellow-100 text-yellow-700" },
  salute:           { l: "Salute",    c: "bg-rose-100 text-rose-700" },
  viaggio:          { l: "Viaggi",    c: "bg-sky-100 text-sky-700" },
  rc_professionale: { l: "RC Prof.",  c: "bg-fuchsia-100 text-fuchsia-700" },
};

function maskCF(cf) {
  // Il backend ritorna già `cf_masked` mascherato; questo helper serve solo
  // come fallback se arrivano CF non mascherati.
  if (!cf) return "—";
  if (String(cf).includes("*")) return cf;
  const s = String(cf).trim().toUpperCase();
  return s.length >= 16 ? `${s.slice(0, 3)}***${s.slice(6)}` : s;
}

function relDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = Math.floor((Date.now() - d.getTime()) / 86400000);
    if (diff <= 0) return "oggi";
    if (diff === 1) return "ieri";
    if (diff < 7) return `${diff}g fa`;
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" });
  } catch { return ""; }
}

function pdfAdminUrl(r) {
  if (r.module === "d_o") return `${API}/admin/do/questionnaires/${r.id}/pdf-unified-admin`;
  if (r.module === "cyber") return `${API}/admin/cyber/pratiche/${r.id}/pdf-unified-admin`;
  if (r.module === "fine_arts") return `${API}/admin/fine-arts/pratiche/${r.id}/pdf-unified-admin`;
  // FASE I/J — Famiglia/Viaggi
  if (["casa", "infortuni", "vita", "salute", "viaggio"].includes(r.module)) {
    return `${API}/admin/${r.module}/pratiche/${r.id}/pdf-unified-admin`;
  }
  return null;
}


// FASE B — Badge assegnato + dropdown "Assegna a"
function AssignmentBadge({ row, dipendenti, onAssign }) {
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const assignedName = row.assigned_to
    ? (dipendenti.find(d => d.user_id === row.assigned_to
                            || d.email === row.assigned_to)?.name || row.assigned_to)
    : null;
  return (
    <div className="relative mt-2" data-testid={`kanban-assignment-${row.id}`}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        disabled={busy}
        className={`w-full text-[10px] rounded px-2 py-1 flex items-center justify-between border transition-all ${
          row.assigned_to
            ? "bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100"
            : "bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100"
        }`}
        data-testid={`kanban-assign-btn-${row.id}`}
      >
        <span className="truncate">
          {row.assigned_to ? `👤 ${assignedName}` : "◯ Non assegnato"}
        </span>
        <span className="text-[9px] opacity-60">▼</span>
      </button>
      {open && (
        <div
          className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-30 max-h-56 overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
          data-testid={`kanban-assign-menu-${row.id}`}
        >
          <button
            type="button"
            onClick={async () => {
              setBusy(true);
              try { await onAssign(row, null); setOpen(false); }
              finally { setBusy(false); }
            }}
            className="w-full text-left px-2 py-1.5 text-[10px] hover:bg-slate-100 border-b border-slate-100"
          >
            ◯ Rimuovi assegnazione
          </button>
          {dipendenti.map((d) => (
            <button
              key={d.user_id}
              type="button"
              onClick={async () => {
                setBusy(true);
                try { await onAssign(row, d.user_id); setOpen(false); }
                finally { setBusy(false); }
              }}
              className="w-full text-left px-2 py-1.5 text-[10px] hover:bg-slate-100 truncate"
              data-testid={`kanban-assign-to-${d.user_id}`}
            >
              👤 {d.name} <span className="text-slate-400">({d.role})</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


function KanbanCard({ r, onView, dragging, dipendenti, onAssign }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `${r.module}::${r.id}`,
    data: { row: r },
  });
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;
  const ramo = RAMO_BADGE[r.module] || { l: r.module, c: "bg-slate-100 text-slate-600" };
  const pdfUrl = pdfAdminUrl(r);
  const nome = [r.nome, r.cognome].filter(Boolean).join(" ").trim() || "—";
  const cfMasked = r.cf_masked || maskCF(r.codice_fiscale);
  const isHot = Number(r.num_richieste_cliente || 0) >= 3;

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`kanban-card-${r.id}`}
      className={`relative bg-white border border-[#E5E2DB] rounded-lg p-3 shadow-sm
                    hover:shadow-md transition-all cursor-grab active:cursor-grabbing
                    ${isDragging || dragging ? "opacity-40" : ""}`}
    >
      {isHot && (
        <div className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full
                          p-1 shadow-md" title="Cliente ad alto potenziale">
          <Flame className="w-3 h-3" />
        </div>
      )}
      <div className="flex items-start gap-2 mb-2">
        <button
          {...attributes} {...listeners}
          data-testid={`kanban-handle-${r.id}`}
          className="text-slate-300 hover:text-slate-500 mt-0.5 cursor-grab">
          <GripVertical className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${ramo.c}`}>
              {ramo.l}
            </span>
            <span className="text-[10px] font-mono text-slate-400 truncate">
              {String(r.id).slice(0, 8)}
            </span>
          </div>
          <div className="font-medium text-sm text-[#0F1F14] truncate" title={nome}>
            {nome}
          </div>
          <div className="text-[11px] text-slate-500 font-mono truncate">
            {cfMasked}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between text-[11px] text-slate-500 pt-2 border-t border-[#E5E2DB]/60">
        <div className="truncate flex-1 mr-2" title={r.compagnia}>
          {r.compagnia || "—"}
        </div>
        <div className="whitespace-nowrap">{relDate(r.created_at)}</div>
      </div>
      <div className="flex items-center gap-1 mt-2">
        {pdfUrl && (
          <a href={pdfUrl} target="_blank" rel="noreferrer"
              data-testid={`kanban-pdf-admin-${r.id}`}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 text-[10px] font-medium
                          bg-[#163321] text-white px-2 py-1 rounded hover:bg-[#1F3A28]"
              title="Scarica PDF Unificato ADMIN">
            <FileText className="w-3 h-3" /> PDF
          </a>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onView(r); }}
          data-testid={`kanban-view-${r.id}`}
          className="inline-flex items-center gap-1 text-[10px] text-slate-600
                      border border-slate-300 px-2 py-1 rounded hover:bg-slate-50">
          <Eye className="w-3 h-3" /> Dettaglio
        </button>
      </div>
      {/* FASE B — Assignment badge/dropdown */}
      <AssignmentBadge row={r} dipendenti={dipendenti || []} onAssign={onAssign} />
    </div>
  );
}


function KanbanColumn({ column, cards, onView, dipendenti, onAssign }) {
  const { setNodeRef, isOver } = useDroppable({ id: column.key });
  const displayed = cards.slice(0, 100);
  const hidden = Math.max(0, cards.length - 100);

  return (
    <div className="flex flex-col bg-[#F7F5EE]/60 rounded-xl min-w-[280px] flex-shrink-0"
          data-testid={`kanban-col-${column.key}`}>
      <div className="px-3 py-3 border-b-2 flex items-center justify-between"
            style={{ borderColor: column.color }}>
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: column.color }} />
          <h3 className="font-heading text-sm text-[#0F1F14]">{column.label}</h3>
        </div>
        <span className="text-xs font-medium text-slate-500 bg-white
                          px-2 py-0.5 rounded-full">
          {cards.length}
        </span>
      </div>
      <div ref={setNodeRef}
            className={`flex-1 p-2 space-y-2 min-h-[200px] overflow-y-auto max-h-[70vh] transition-colors
                        ${isOver ? "bg-blue-50/50 ring-2 ring-blue-300 ring-inset" : ""}`}>
        {displayed.map((r) => (
          <KanbanCard key={`${r.module}-${r.id}`} r={r} onView={onView}
                        dipendenti={dipendenti} onAssign={onAssign} />
        ))}
        {hidden > 0 && (
          <div className="text-center text-[11px] text-slate-500 italic py-2">
            … e altre <b>{hidden}</b> richieste. Usa la vista tabellare per la lista completa.
          </div>
        )}
        {cards.length === 0 && (
          <div className="text-center text-xs text-slate-400 italic py-6">
            Nessuna richiesta
          </div>
        )}
      </div>
    </div>
  );
}


export default function KanbanBoard({ rows, onView, onAfterMove }) {
  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: { distance: 5 },
  }));
  const [activeRow, setActiveRow] = useState(null);
  const [lastMove, setLastMove] = useState(null);
  const [dipendenti, setDipendenti] = useState([]);

  // FASE B — Fetch team members for assignment dropdown
  React.useEffect(() => {
    axios.get(`${API}/admin/team/dipendenti-attivi`, { withCredentials: true })
      .then((r) => setDipendenti(r.data.users || []))
      .catch(() => setDipendenti([]));
  }, []);

  // FASE B — Assign handler
  const handleAssign = useCallback(async (row, targetUserId) => {
    try {
      if (targetUserId === null) {
        await axios.post(`${API}/admin/richieste/${row.id}/unassign`,
                            { module: row.module },
                            { withCredentials: true });
        toast.success("Assegnazione rimossa");
      } else {
        await axios.post(`${API}/admin/richieste/${row.id}/assign`,
                            { module: row.module, user_id: targetUserId },
                            { withCredentials: true });
        const d = dipendenti.find(x => x.user_id === targetUserId);
        toast.success(`Assegnata a ${d?.name || targetUserId}`);
      }
      onAfterMove?.(row.id, row.status_unified);
    } catch (e) {
      const msg = e.response?.data?.detail || "Errore assegnazione";
      toast.error(msg);
    }
  }, [dipendenti, onAfterMove]);

  // Raggruppa per status unificato
  const grouped = useMemo(() => {
    const g = Object.fromEntries(KANBAN_COLUMNS.map((c) => [c.key, []]));
    for (const r of rows) {
      const s = r.status_unified || r.status_unificato || "nuovo";
      if (g[s]) g[s].push(r);
    }
    return g;
  }, [rows]);

  const persistStatus = useCallback(async (row, newStatus, prevStatus) => {
    try {
      await axios.post(`${API}/admin/richieste/bulk-action`, {
        action: "change_status",
        new_status: newStatus,
        selected_ids: [{ module: row.module, id: row.id }],
      }, { withCredentials: true });
      onAfterMove?.(row.id, newStatus);

      // Undo toast (5s)
      toast.success(
        `Spostato in "${KANBAN_COLUMNS.find((c) => c.key === newStatus)?.label}"`,
        {
          duration: 5000,
          action: {
            label: (<span className="inline-flex items-center gap-1">
                      <Undo2 className="w-3 h-3" /> Annulla</span>),
            onClick: () => persistStatus(row, prevStatus, newStatus),
          },
        }
      );
    } catch (e) {
      console.error(e);
      toast.error("Errore aggiornamento status — ripristino card");
      // Rollback: onAfterMove col vecchio status
      onAfterMove?.(row.id, prevStatus);
    }
  }, [onAfterMove]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={(e) => setActiveRow(e.active.data.current?.row || null)}
      onDragEnd={(e) => {
        setActiveRow(null);
        const overId = e.over?.id;
        const row = e.active.data.current?.row;
        if (!row || !overId || !KANBAN_COLUMNS.find((c) => c.key === overId)) return;
        const newStatus = String(overId);
        const prevStatus = row.status_unified || row.status_unificato;
        if (newStatus === prevStatus) return;
        // Optimistic UI: aggiorna subito il local state via onAfterMove
        onAfterMove?.(row.id, newStatus);
        setLastMove({ row, prevStatus });
        // Persist
        persistStatus(row, newStatus, prevStatus);
      }}
    >
      <div className="flex gap-3 overflow-x-auto pb-4"
            data-testid="kanban-board">
        {KANBAN_COLUMNS.map((col) => (
          <KanbanColumn key={col.key} column={col}
                          cards={grouped[col.key]} onView={onView}
                          dipendenti={dipendenti} onAssign={handleAssign} />
        ))}
      </div>
      <DragOverlay>
        {activeRow ? (
          <div className="opacity-90">
            <KanbanCard r={activeRow} onView={() => {}} dragging
                          dipendenti={dipendenti} onAssign={handleAssign} />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
